/**
 * "Manage" dropdown — anchored to the Manage toolbar button.
 *
 * Shows three actions: New Map (always enabled), Rename Map, Delete Map
 * (both disabled when no map is loaded). ESC and outside-click close the menu.
 * Picking an item closes the menu and invokes the corresponding callback.
 *
 * Visual style reuses `.context-menu` from styles.css.
 */

export interface ManageMenuActions {
  hasCurrentMap: boolean;
  onNewMap: () => void;
  onRenameMap: () => void;
  onDeleteMap: () => void;
}

/** Open the menu directly below the given anchor element. Idempotent. */
export function openManageMenu(anchor: HTMLElement, actions: ManageMenuActions): void {
  // Close any existing instance first.
  dismiss();

  const menu = document.createElement('div');
  menu.className = 'context-menu manage-menu';
  menu.setAttribute('role', 'menu');

  addItem(menu, 'New Map', true, () => { dismiss(); actions.onNewMap(); });
  addItem(menu, 'Rename Map', actions.hasCurrentMap, () => { dismiss(); actions.onRenameMap(); });
  addItem(menu, 'Delete Map', actions.hasCurrentMap, () => { dismiss(); actions.onDeleteMap(); }, true);

  document.body.appendChild(menu);
  active = menu;

  // Position below the anchor; clamp to viewport.
  const rect = anchor.getBoundingClientRect();
  menu.style.left = `${rect.left}px`;
  menu.style.top = `${rect.bottom + 4}px`;
  const menuRect = menu.getBoundingClientRect();
  if (menuRect.right > window.innerWidth) {
    menu.style.left = `${Math.max(0, window.innerWidth - menuRect.width - 4)}px`;
  }
  if (menuRect.bottom > window.innerHeight) {
    menu.style.top = `${Math.max(0, rect.top - menuRect.height - 4)}px`;
  }

  // Defer dismiss handlers so the originating click doesn't immediately close.
  setTimeout(() => {
    document.addEventListener('mousedown', onOutsideMouseDown);
    document.addEventListener('keydown', onEscape);
  }, 0);
}

let active: HTMLElement | null = null;

function dismiss(): void {
  if (active === null) return;
  active.remove();
  active = null;
  document.removeEventListener('mousedown', onOutsideMouseDown);
  document.removeEventListener('keydown', onEscape);
}

function onOutsideMouseDown(e: MouseEvent): void {
  if (active === null) return;
  if (e.target instanceof Node && active.contains(e.target)) return;
  dismiss();
}

function onEscape(e: KeyboardEvent): void {
  if (e.key === 'Escape') dismiss();
}

function addItem(
  menu: HTMLElement,
  label: string,
  enabled: boolean,
  onClick: () => void,
  danger = false,
): void {
  const item = document.createElement('button');
  item.type = 'button';
  item.className = 'context-menu-item';
  if (danger) item.classList.add('is-danger');
  item.textContent = label;
  item.setAttribute('role', 'menuitem');
  item.disabled = !enabled;
  if (enabled) {
    item.addEventListener('click', onClick);
  }
  menu.appendChild(item);
}
